/**
 * ServeFirst staff teams. A person's team is stored on their admin app account in the
 * sign-in service, and only people with a team can rank product priorities.
 *
 * The keys must match the sign-in service's team list exactly (it validates them), and
 * the product priorities API uses the same keys for team weights.
 */
export const TEAM_KEYS = [
  'leadership',
  'product',
  'design',
  'engineering',
  'sales',
  'account_management',
  'operations',
  'marketing',
  'other',
] as const;

export type TeamKey = (typeof TEAM_KEYS)[number];

export const TEAM_LABELS: Record<TeamKey, string> = {
  leadership: 'Leadership',
  product: 'Product',
  design: 'Design',
  engineering: 'Engineering',
  sales: 'Sales',
  account_management: 'Account management',
  operations: 'Operations',
  marketing: 'Marketing',
  other: 'Other',
};

export const TEAM_OPTIONS = TEAM_KEYS.map((value) => ({ value, label: TEAM_LABELS[value] }));

/** True for a known team key; anything else (null, unknown strings) is "no team". */
export function isTeamKey(value: unknown): value is TeamKey {
  return typeof value === 'string' && (TEAM_KEYS as readonly string[]).includes(value);
}

/** Display label for a stored team value, or null when the person has no team. */
export function teamLabel(value: unknown): string | null {
  return isTeamKey(value) ? TEAM_LABELS[value] : null;
}
